import React from 'react';
import type {
  RbacDataScope,
  RbacPermissionAction,
  RbacPrincipalType,
} from '../../types/permissions';
import { usePermissions } from '../providers/PermissionContext';
import { ForbiddenPage, PermissionLoadingPage } from './ForbiddenPage';

export interface RequirePermissionProps {
  permissions?: string[];
  menuPath?: string;
  principalTypes?: RbacPrincipalType[];
  action?: RbacPermissionAction;
  scope?: RbacDataScope;
  match?: 'any' | 'all';
  requireRbacEnabled?: boolean;
  children: React.ReactNode;
}

/** Screen-level guard. Backend middleware remains the authoritative check. */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permissions,
  menuPath,
  principalTypes,
  action = 'view',
  scope,
  match = 'any',
  requireRbacEnabled = false,
  children,
}) => {
  const access = usePermissions();

  if (access.isLoading) return <PermissionLoadingPage />;

  const principalAllowed =
    !principalTypes ||
    (access.principal !== null && principalTypes.includes(access.principal.type));
  const check = (permission: string) => access.canAction(permission, action, scope);
  const explicitPermissionAllowed = permissions
    ? permissions.length === 0 ||
      (match === 'all' ? permissions.every(check) : permissions.some(check))
    : menuPath !== undefined;
  const menuAllowed = menuPath
    ? access.canAccessPath(menuPath, action, scope)
    : permissions !== undefined;
  const featureAllowed =
    !requireRbacEnabled || (access.enabled && !access.isBypassed);
  const allowed =
    featureAllowed && principalAllowed && explicitPermissionAllowed && menuAllowed;

  if (!access.isReady || access.isError || !allowed) {
    return (
      <ForbiddenPage
        onRetry={access.isReady ? undefined : () => void access.refresh()}
        isRetrying={access.isFetching}
      />
    );
  }

  return <>{children}</>;
};

export default RequirePermission;
